import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";
import ScrollToTop from "./Components/ScrollToTop";
import ProtectedRoute from "./Auth/ProtectedRoute";
import { useAuth } from "./Auth/AuthContext";
import PageNotFound from "./Components/PageNotFound";
import { ThemeProvider } from "./ThemeContext";

// Modals are kept static because they control UI over the current page
import AuthModal from "./Auth/AuthModal";
import OrganizerProfileModal from "./Auth/OrganizerProfileModal";

// Lazy Loaded Pages
const Home = lazy(() => import("./Pages/Home"));
const Events = lazy(() => import("./Pages/Events"));
const ForOrganizers = lazy(() => import("./Pages/ForOrganizers"));
const CreateEventModal = lazy(() => import("./Modals/CreateEventModal"));
const EventView = lazy(() => import("./Pages/EventView"));
const Checkout = lazy(() => import("./Pages/Checkout"));
const OrganizerDashboard = lazy(() => import("./Pages/OrganizerDashboard"));
const AboutUs = lazy(() => import("./Components/AboutUs"));
const MyProfile = lazy(() => import("./Components/MyProfile"));
const ContactSupport = lazy(() => import("./Pages/ContactSupport"));
const Faq = lazy(() => import("./Pages/Faq"));
const Pricing = lazy(() => import("./Pages/Pricing"));
const Wishlist = lazy(() => import("./Pages/Wishlist"));
const EventViewForOrganizer = lazy(() => import("./Pages/EventViewForOrganizer"));
const ResetPassword = lazy(() => import("./Pages/ResetPassword"));
const TermsAndConditionsForOrganizers = lazy(() => import("./Pages/TermsAndConditionsForOrganizers"));
const EventsReview = lazy(() => import("./Pages/EventsReview"));
const PrivacyPolicy = lazy(() => import("./Pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./Pages/RefundPolicy"));
const TermsOfService = lazy(() => import("./Pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./Pages/CookiePolicy"));
const QrCodeSecurityPolicy = lazy(() => import("./Pages/QrCodeSecurityPolicy"));
const MyTicket = lazy(() => import("./Components/MyTicket"));
const GoogleCallback = lazy(() => import("./Auth/GoogleCallback"));
const VerifyEmail = lazy(() => import("./Pages/VerifyEmail"));

// Admin Lazy Loaded Pages
const AdminLayout = lazy(() => import("./Admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./Admin/AdminDashboard"));
const AdminLedger = lazy(() => import("./Admin/AdminLedger"));
const AdminEntries = lazy(() => import("./Admin/AdminEntries"));
const AdminOrders = lazy(() => import("./Admin/AdminOrders"));
const AdminRefunds = lazy(() => import("./Admin/AdminRefunds"));
const AdminUsers = lazy(() => import("./Admin/AdminUsers"));
const AdminEvents = lazy(() => import("./Admin/AdminEvents"));
const AdminSettings = lazy(() => import("./Admin/AdminSettings"));
// Simple Loader Component
const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <div className="spinner-border" style={{ color: '#9810FA', width: '3rem', height: '3rem' }} role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const AppContent = () => {
  const [activeTab, setActiveTab] = useState("home");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showOrganizerModal, setShowOrganizerModal] = useState(false);
  const { needsOrganizerProfile } = useAuth();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/control');

  // Handle global body padding for Admin Layout (removing Navbar.css 70px padding)
  useEffect(() => {
    if (isAdminRoute) {
      document.body.style.paddingTop = '0px';
    } else {
      document.body.style.paddingTop = '70px'; // Value from Navbar.css
    }
  }, [isAdminRoute]);

  useEffect(() => {
    if (needsOrganizerProfile) {
      setShowOrganizerModal(true);
    }
  }, [needsOrganizerProfile]);

  useEffect(() => {
    const handleOpenLoginModal = () => {
      setIsLogin(true);
      setShowAuthModal(true);
    };
    window.addEventListener("openLoginModal", handleOpenLoginModal);
    return () => window.removeEventListener("openLoginModal", handleOpenLoginModal);
  }, []);

  return (
    <ThemeProvider>
      <div className="app-container">
        <ScrollToTop />
        <ToastContainer position="bottom-left" />

        {!isAdminRoute && (
          <Navbar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onAuthClick={() => {
              setIsLogin(true);
              setShowAuthModal(true);
            }}
          />
        )}

        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Main pages */}
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="*" element={<PageNotFound />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/event-view/:id" element={<EventView />} />
            <Route path="/for-organizers" element={<ForOrganizers />} />

            {/* Protected routes - require authentication */}
            <Route
              path="/control/*"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="ledger" element={<AdminLedger />} />
              <Route path="entries" element={<AdminEntries />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="refunds" element={<AdminRefunds />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="activity" element={<div className="tw-p-6">Activity logs coming soon...</div>} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredRole="organizer">
                  <OrganizerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/event-view-for-organizer/:id"
              element={
                <ProtectedRoute requiredRole="organizer">
                  <EventViewForOrganizer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout"
              element={
                <ProtectedRoute requiredRole={["organizer", "user"]}>
                  <Checkout />
                </ProtectedRoute>
              }
            />

            {/* Other pages */}
            <Route path="/signup" element={<AuthModal />} />
            <Route path="/create-event-modal" element={<CreateEventModal />} />
            <Route
              path="/events/events-review/:slug"
              element={<EventsReview />}
            />

            {/* Footer Pages */}
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute requiredRole={["organizer", "user"]}>
                  <MyProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-tickets"
              element={
                <ProtectedRoute requiredRole={["organizer", "user"]}>
                  <MyTicket />
                </ProtectedRoute>
              }
            />
            <Route
              path="/wishlist"
              element={
                <ProtectedRoute>
                  <Wishlist />
                </ProtectedRoute>
              }
            />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-support" element={<ContactSupport />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route
              path="/terms-and-conditions-for-organizers"
              element={<TermsAndConditionsForOrganizers />}
            />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route
              path="/qr-code-security-policy"
              element={<QrCodeSecurityPolicy />}
            />
            <Route path="/pricing" element={<Pricing />} />
            <Route
              path="/my-profile/reset-password"
              element={<ResetPassword />}
            />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            <Route path="/api/accounts/email-verify/confirm" element={<VerifyEmail />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
          </Routes>
        </Suspense>

        {/* Modals */}
        <AuthModal
          show={showAuthModal}
          handleClose={() => setShowAuthModal(false)}
          isLogin={isLogin}
          setIsLogin={setIsLogin}
        />

        <OrganizerProfileModal
          show={showOrganizerModal}
          handleClose={() => setShowOrganizerModal(false)}
        />

        {!isAdminRoute && <Footer />}
      </div>
    </ThemeProvider>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
